import { getMerchantBusinessAvatarCoffee } from '@assets/illustrations';
import { resolvePreferredActiveBusinessId } from '@modules/merchant/layout/businessStatus';
import { buildRestaurantUpdatePayload } from '@modules/merchant/manage-business/utils/buildRestaurantUpdatePayload';
import {
  formatRestaurantLocation,
  mapRestaurantDetail,
} from '@modules/merchant/manage-business/utils/mapRestaurantDetail';
import { mapReviews } from '@modules/merchant/manage-business/utils/mapReviews';
import { resolveTeamPermission } from '@modules/merchant/team/utils/resolveTeamPermission';
import {
  MERCHANT_CONFIG_DESTINATION,
  parseTotalBusinessCount,
  resolveMerchantConfigDestination,
} from '@modules/merchant/utils/resolveMerchantConfigDestination';
import { logoutUser, setUser } from '@redux/auth/authSlice';
import { submitBusinessClaim, setProfileCompleted } from '@redux/onboarding/onboardingSlice';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { merchantAuthApi } from '@services/api';
import encryptedStorage from '@storage/encryptedStorage';
import { getApiErrorMessage } from '@utils/apiError';

const DEFAULT_AVATAR = getMerchantBusinessAvatarCoffee();
const ACTIVE_BUSINESS_STORAGE_KEY = 'merchantActiveBusiness';

const persistActiveBusinessSnapshot = (business) => {
  if (!business?.id) return;

  encryptedStorage.setItem(ACTIVE_BUSINESS_STORAGE_KEY, {
    id: business.id,
    name: business.name,
    avatar: business.avatar,
    role: business.role || '',
  });
};

const readPersistedActiveBusinessSnapshot = () => {
  const stored = encryptedStorage.getItem(ACTIVE_BUSINESS_STORAGE_KEY);
  if (!stored?.id) return null;

  return {
    id: String(stored.id),
    name: stored.name || 'Business',
    avatar: stored.avatar || DEFAULT_AVATAR,
    role: String(stored.role || '')
      .trim()
      .toUpperCase(),
  };
};

const clearPersistedActiveBusinessSnapshot = () => {
  encryptedStorage.removeItem(ACTIVE_BUSINESS_STORAGE_KEY);
};

const persistedActiveBusiness = readPersistedActiveBusinessSnapshot();

const syncActiveBusinessSnapshot = (state) => {
  const activeBusiness = state.businesses.find(
    (item) => String(item.id) === String(state.activeBusinessId),
  );

  if (activeBusiness) {
    state.businessSwitcherSnapshot = activeBusiness;
    persistActiveBusinessSnapshot(activeBusiness);
    return;
  }

  // Keep the current snapshot when the active id is missing from the list
  // (search filter / in-flight refetch). Never fall back to businesses[0].
  if (
    state.activeBusinessId &&
    state.businessSwitcherSnapshot &&
    String(state.businessSwitcherSnapshot.id) === String(state.activeBusinessId)
  ) {
    return;
  }

  if (!state.activeBusinessId) {
    state.businessSwitcherSnapshot = null;
    clearPersistedActiveBusinessSnapshot();
  }
};

const pickFirst = (...values) => values.find((value) => value != null && value !== '');

/** `null` when the list item has an empty plan key. `undefined` when the field is absent. */
const readListPlanKey = (restaurant = {}) => {
  if (Object.prototype.hasOwnProperty.call(restaurant, 'planKey')) {
    return String(restaurant.planKey ?? '').trim() || null;
  }

  const subscription = restaurant.subscription;
  if (
    subscription &&
    typeof subscription === 'object' &&
    Object.prototype.hasOwnProperty.call(subscription, 'planKey')
  ) {
    return String(subscription.planKey ?? '').trim() || null;
  }

  return undefined;
};

const isTruthyFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';

/** Restaurant list `trialAvailable` drives the Manage Subscription chip. */
const readTrialAvailable = (restaurant = {}) => {
  const subscription =
    restaurant.subscription && typeof restaurant.subscription === 'object'
      ? restaurant.subscription
      : null;
  const subscriptionStatus = String(subscription?.status ?? subscription?.state ?? '')
    .trim()
    .toLowerCase();
  // status/state "none" means no subscription — never show Free trial available.
  if (subscriptionStatus === 'none') return false;

  if (Object.prototype.hasOwnProperty.call(restaurant, 'trialAvailable')) {
    return isTruthyFlag(restaurant.trialAvailable);
  }

  if (subscription && Object.prototype.hasOwnProperty.call(subscription, 'trialAvailable')) {
    return isTruthyFlag(subscription.trialAvailable);
  }

  return false;
};

export const mapRestaurant = (restaurant = {}) => {
  const id = String(
    pickFirst(restaurant.id, restaurant.restaurantId, restaurant.placeId, restaurant.place_id) ||
      '',
  );
  const planKey = readListPlanKey(restaurant);

  return {
    id,
    placeId: pickFirst(restaurant.placeId, restaurant.place_id, id) || '',
    name:
      pickFirst(restaurant.name, restaurant.restaurantName, restaurant.businessName) || 'Business',
    location: formatRestaurantLocation(restaurant),
    avatar:
      pickFirst(
        restaurant.thumbnail,
        restaurant.avatar,
        restaurant.image,
        restaurant.imageUrl,
        restaurant.photoUrl,
        restaurant.profilePicUrl,
        restaurant.logoUrl,
      ) || DEFAULT_AVATAR,
    status: pickFirst(restaurant.status, restaurant.approvalStatus) || '',
    manageBusiness: resolveTeamPermission(
      [restaurant.permissions, restaurant.userPermissions, restaurant],
      'manageBusiness',
      true,
    ),
    manageFeed: resolveTeamPermission(
      [restaurant.permissions, restaurant.userPermissions, restaurant],
      'manageFeed',
      true,
    ),
    manageNudge: resolveTeamPermission(
      [restaurant.permissions, restaurant.userPermissions, restaurant],
      'manageNudge',
      true,
    ),
    manageTeam: resolveTeamPermission(
      [restaurant.permissions, restaurant.userPermissions, restaurant],
      'manageTeam',
      true,
    ),
    role: String(pickFirst(restaurant.role, restaurant.userRole, restaurant.joinedAs) || '')
      .trim()
      .toUpperCase(),
    ghlLocationId: String(pickFirst(restaurant.ghlLocationId, restaurant.ghl_location_id) || ''),
    subscription: restaurant.subscription ?? null,
    trialAvailable: readTrialAvailable(restaurant),
    ...(planKey !== undefined ? { planKey } : {}),
    raw: restaurant,
  };
};

const extractRestaurantList = (payload) => {
  const data = payload?.data ?? payload;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.restaurants)) return data.restaurants;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.list)) return data.list;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(payload?.restaurants)) return payload.restaurants;

  return [];
};

export const fetchRestaurants = createAsyncThunk(
  'merchant/fetchRestaurants',
  async (search = '', { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await merchantAuthApi.listRestaurants({ search: search || '' });
      return extractRestaurantList(response.data)
        .map(mapRestaurant)
        .filter((item) => item.id);
    } catch (error) {
      const status = error?.response?.status;
      const message = getApiErrorMessage(error, 'Failed to load businesses');

      if (status === 403) {
        const currentUser = getState().auth.user;
        if (currentUser) {
          dispatch(
            setUser({
              ...currentUser,
              hasApprovedBusiness: false,
            }),
          );
        }

        return rejectWithValue({ status: 403, message });
      }

      return rejectWithValue({ status: status || null, message });
    }
  },
);

export const fetchAppConfig = createAsyncThunk(
  'merchant/fetchAppConfig',
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await merchantAuthApi.getAppConfig();
      const data = response.data?.data ?? response.data ?? null;

      if (data) {
        const currentUser = getState().auth.user;
        if (currentUser) {
          dispatch(
            setUser({
              ...currentUser,
              hasApprovedBusiness: Boolean(data.hasApprovedBusiness),
            }),
          );
        }

        if (typeof data.isProfileComplete === 'boolean') {
          dispatch(setProfileCompleted(data.isProfileComplete));
        }
      }

      return data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to load app config'));
    }
  },
);

export const fetchRestaurantDetail = createAsyncThunk(
  'merchant/fetchRestaurantDetail',
  async (arg, { rejectWithValue }) => {
    const restaurantId = typeof arg === 'string' ? arg : arg?.restaurantId;
    const tab = typeof arg === 'object' ? arg?.tab : undefined;

    if (!restaurantId) {
      return rejectWithValue('Restaurant id is required');
    }

    try {
      const payload = { restaurantId };
      if (tab) {
        payload.tab = tab;
      }

      const response = await merchantAuthApi.getRestaurantDetail(payload);
      const detail = mapRestaurantDetail(response.data);
      return {
        ...detail,
        id: detail.id || String(restaurantId),
        tab: tab || null,
      };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to load restaurant details'));
    }
  },
);

export const updateRestaurantDetail = createAsyncThunk(
  'merchant/updateRestaurantDetail',
  async (arg, { getState, rejectWithValue }) => {
    const restaurantId = typeof arg === 'string' ? arg : arg?.restaurantId;
    const overrides = (typeof arg === 'object' && arg?.overrides) || {};

    if (!restaurantId) {
      return rejectWithValue('Restaurant id is required');
    }

    const detail = getState().merchant.restaurantDetail;
    const payload = buildRestaurantUpdatePayload({ restaurantId, detail, overrides });

    try {
      await merchantAuthApi.updateRestaurant(payload);
      return { overrides };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update restaurant'));
    }
  },
);

const REVIEWS_DEFAULT_LIMIT = 15;
const REVIEWS_SORT_OPTIONS = ['latest', 'highRating', 'lowRating'];

export const fetchRestaurantReviews = createAsyncThunk(
  'merchant/fetchRestaurantReviews',
  async (arg = {}, { rejectWithValue }) => {
    const restaurantId = typeof arg === 'string' ? arg : arg?.restaurantId;

    if (!restaurantId) {
      return rejectWithValue('Restaurant id is required');
    }

    const page = Math.max(1, Number(arg?.page) || 1);
    const rawLimit = Number(arg?.limit) || REVIEWS_DEFAULT_LIMIT;
    const limit = Math.min(100, Math.max(1, rawLimit));
    const sortBy = REVIEWS_SORT_OPTIONS.includes(arg?.sortBy) ? arg.sortBy : 'latest';
    const onlyPhotoReview = Boolean(arg?.onlyPhotoReview);

    try {
      const response = await merchantAuthApi.listReviews({
        restaurantId,
        page,
        limit,
        sortBy,
        onlyPhotoReview,
      });

      return {
        ...mapReviews(response.data),
        sortBy,
        onlyPhotoReview,
        append: page > 1,
      };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to load reviews'));
    }
  },
);

const merchantSlice = createSlice({
  name: 'merchant',
  initialState: {
    businesses: [],
    activeBusinessId: persistedActiveBusiness?.id ?? null,
    businessSwitcherSnapshot: persistedActiveBusiness,
    businessesLoading: false,
    businessesHydrated: false,
    businessesError: null,
    restaurantDetail: null,
    restaurantDetailTab: null,
    restaurantDetailLoading: false,
    restaurantDetailSaving: false,
    restaurantDetailError: null,
    reviews: [],
    reviewsSummary: null,
    reviewsPagination: null,
    reviewsLoading: false,
    reviewsError: null,
    appConfig: null,
    appConfigLoading: false,
    appConfigError: null,
    appConfigHydrated: false,
  },
  reducers: {
    setActiveBusinessId: (state, action) => {
      // List not loaded yet — do not wipe the current selection. fetchRestaurants
      // validates the id against the latest list once it arrives.
      if (!state.businesses.length) {
        return;
      }

      const preferredId = resolvePreferredActiveBusinessId(state.businesses, action.payload);
      state.activeBusinessId = preferredId ?? action.payload;

      const business = state.businesses.find(
        (item) => String(item.id) === String(state.activeBusinessId),
      );
      if (business) {
        state.businessSwitcherSnapshot = business;
        persistActiveBusinessSnapshot(business);
      } else {
        state.activeBusinessId = null;
        state.businessSwitcherSnapshot = null;
        clearPersistedActiveBusinessSnapshot();
      }
    },
    switchActiveBusiness: (state, action) => {
      const payload = action.payload;
      const businessId = String(
        (payload && typeof payload === 'object' ? payload.id : payload) ?? '',
      );
      if (!businessId) return;

      const business = state.businesses.find((item) => String(item.id) === businessId);
      const snapshot = business || {
        id: businessId,
        name:
          (payload && typeof payload === 'object' && payload.name) ||
          (state.businessSwitcherSnapshot &&
          String(state.businessSwitcherSnapshot.id) === businessId
            ? state.businessSwitcherSnapshot.name
            : 'Business'),
        avatar:
          (payload && typeof payload === 'object' && payload.avatar) ||
          (state.businessSwitcherSnapshot &&
          String(state.businessSwitcherSnapshot.id) === businessId
            ? state.businessSwitcherSnapshot.avatar
            : DEFAULT_AVATAR),
      };

      state.activeBusinessId = business?.id ?? businessId;
      state.businessSwitcherSnapshot = snapshot;
      persistActiveBusinessSnapshot(snapshot);

      state.restaurantDetail = null;
      state.restaurantDetailTab = null;
      state.restaurantDetailLoading = false;
      state.restaurantDetailSaving = false;
      state.restaurantDetailError = null;
      state.reviews = [];
      state.reviewsSummary = null;
      state.reviewsPagination = null;
      state.reviewsLoading = false;
      state.reviewsError = null;
    },
    clearRestaurantDetail: (state) => {
      state.restaurantDetail = null;
      state.restaurantDetailTab = null;
      state.restaurantDetailLoading = false;
      state.restaurantDetailSaving = false;
      state.restaurantDetailError = null;
    },
    clearReviews: (state) => {
      state.reviews = [];
      state.reviewsSummary = null;
      state.reviewsPagination = null;
      state.reviewsLoading = false;
      state.reviewsError = null;
    },
    /**
     * Admin-only: seeds this slice with a single mock business so reused merchant
     * components (Dashboard/Nudges/Team/Feed) render against it directly, without
     * going through the real `fetchAppConfig`/`fetchRestaurants` network bootstrap
     * (which requires a real merchant session and doesn't apply to admin's mock
     * business records). Not dispatched anywhere in the merchant portal itself.
     */
    seedAdminBusinessContext: (state, action) => {
      const business = action.payload;
      if (!business?.id) return;

      state.appConfigHydrated = true;
      state.appConfig = {
        hasApprovedBusiness: true,
        totalBusinessCount: 1,
        isProfileComplete: true,
      };
      state.appConfigLoading = false;
      state.appConfigError = null;

      state.businessesHydrated = true;
      state.businessesLoading = false;
      state.businessesError = null;
      state.businesses = [business];
      state.activeBusinessId = business.id;
      state.businessSwitcherSnapshot = business;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRestaurants.pending, (state) => {
        state.businessesError = null;
        state.businessesLoading = true;
      })
      .addCase(fetchRestaurants.fulfilled, (state, action) => {
        state.businessesLoading = false;
        state.businessesHydrated = true;
        state.businesses = action.payload;

        const isUnfiltered = !String(action.meta?.arg ?? '').trim();

        if (!action.payload.length) {
          // Filtered search can return empty — keep the active selection.
          if (isUnfiltered) {
            state.activeBusinessId = null;
          }
        } else {
          const currentStillValid = action.payload.some(
            (item) => String(item.id) === String(state.activeBusinessId),
          );

          if (!currentStillValid) {
            // Only re-pick when this is the full list (or we have no selection).
            // Filtered results must not steal a notification / switcher selection.
            if (isUnfiltered || !state.activeBusinessId) {
              state.activeBusinessId = resolvePreferredActiveBusinessId(
                action.payload,
                state.activeBusinessId,
              );
            }
          }
        }

        syncActiveBusinessSnapshot(state);
      })
      .addCase(fetchRestaurants.rejected, (state, action) => {
        state.businessesLoading = false;
        state.businessesHydrated = true;

        const payload = action.payload;
        const isForbidden = payload?.status === 403;
        state.businessesError =
          typeof payload === 'string' ? payload : payload?.message || 'Failed to load businesses';

        if (isForbidden) {
          state.businesses = [];
          state.activeBusinessId = null;
          state.businessSwitcherSnapshot = null;
          clearPersistedActiveBusinessSnapshot();
        }
      })
      .addCase(fetchRestaurantDetail.pending, (state) => {
        state.restaurantDetailLoading = true;
        state.restaurantDetailError = null;
      })
      .addCase(fetchRestaurantDetail.fulfilled, (state, action) => {
        state.restaurantDetailLoading = false;
        state.restaurantDetail = action.payload;
        state.restaurantDetailTab = action.payload?.tab ?? null;
      })
      .addCase(fetchRestaurantDetail.rejected, (state, action) => {
        state.restaurantDetailLoading = false;
        state.restaurantDetailError = action.payload;
      })
      .addCase(updateRestaurantDetail.pending, (state) => {
        state.restaurantDetailSaving = true;
        state.restaurantDetailError = null;
      })
      .addCase(updateRestaurantDetail.fulfilled, (state, action) => {
        state.restaurantDetailSaving = false;

        const overrides = action.payload?.overrides ?? {};
        if (state.restaurantDetail) {
          state.restaurantDetail.raw = { ...state.restaurantDetail.raw, ...overrides };

          if ('name' in overrides) {
            state.restaurantDetail.name = overrides.name ?? '';
          }
          if ('phone' in overrides) {
            state.restaurantDetail.phone = overrides.phone ?? '';
          }
          if ('email' in overrides) {
            state.restaurantDetail.email = overrides.email ?? '';
          }
          if ('price' in overrides) {
            state.restaurantDetail.priceTier =
              overrides.price == null || overrides.price === '' ? '' : String(overrides.price);
          }
          if ('thumbnail' in overrides) {
            state.restaurantDetail.avatar = overrides.thumbnail ?? '';
            state.restaurantDetail.coverImage = overrides.thumbnail ?? '';
          }
          if ('orderOnline' in overrides) {
            state.restaurantDetail.orderLink = overrides.orderOnline ?? '';
          }
          if ('reserveTable' in overrides) {
            state.restaurantDetail.reservationLink = overrides.reserveTable ?? '';
          }
          if ('address' in overrides) {
            state.restaurantDetail.streetAddress = overrides.address ?? '';
          }
          if ('zipcode' in overrides) {
            state.restaurantDetail.zipCode = overrides.zipcode ?? '';
          }
          if ('city' in overrides) {
            state.restaurantDetail.city = overrides.city ?? '';
          }
          if ('state' in overrides) {
            state.restaurantDetail.state = overrides.state ?? '';
          }
          if ('country' in overrides) {
            state.restaurantDetail.country = overrides.country ?? '';
          }
          if ('latitude' in overrides) {
            state.restaurantDetail.latitude = overrides.latitude;
          }
          if ('longitude' in overrides) {
            state.restaurantDetail.longitude = overrides.longitude;
          }
          if (overrides.formatedAddress && typeof overrides.formatedAddress === 'object') {
            const formatted = overrides.formatedAddress;
            state.restaurantDetail.streetAddress =
              formatted.addressLine1 ?? state.restaurantDetail.streetAddress;
            state.restaurantDetail.addressLine2 =
              formatted.addressLine2 ?? state.restaurantDetail.addressLine2;
            state.restaurantDetail.city = formatted.city ?? state.restaurantDetail.city;
            state.restaurantDetail.state = formatted.state ?? state.restaurantDetail.state;
            state.restaurantDetail.country = formatted.country ?? state.restaurantDetail.country;
            state.restaurantDetail.zipCode =
              formatted.zipcode ?? formatted.zipCode ?? state.restaurantDetail.zipCode;
            state.restaurantDetail.location = formatRestaurantLocation({
              ...state.restaurantDetail,
              formatedAddress: {
                ...(state.restaurantDetail.raw?.formatedAddress || {}),
                ...formatted,
              },
            });
          }
        }
      })
      .addCase(updateRestaurantDetail.rejected, (state, action) => {
        state.restaurantDetailSaving = false;
        state.restaurantDetailError = action.payload;
      })
      .addCase(fetchRestaurantReviews.pending, (state) => {
        state.reviewsLoading = true;
        state.reviewsError = null;
      })
      .addCase(fetchRestaurantReviews.fulfilled, (state, action) => {
        const { reviews, append, ...summary } = action.payload;
        state.reviewsLoading = false;
        state.reviews = append ? [...state.reviews, ...reviews] : reviews;
        state.reviewsSummary = {
          averageRating: summary.averageRating,
          totalReviews: summary.totalReviews,
          distribution: summary.distribution,
          sortBy: summary.sortBy,
          onlyPhotoReview: summary.onlyPhotoReview,
        };
        state.reviewsPagination = {
          page: summary.page,
          limit: summary.limit,
          totalPages: summary.totalPages,
        };
      })
      .addCase(fetchRestaurantReviews.rejected, (state, action) => {
        state.reviewsLoading = false;
        state.reviewsError = action.payload;
      })
      .addCase(fetchAppConfig.pending, (state) => {
        state.appConfigLoading = true;
        state.appConfigError = null;
      })
      .addCase(fetchAppConfig.fulfilled, (state, action) => {
        state.appConfigLoading = false;
        state.appConfigHydrated = true;
        state.appConfig = action.payload;
      })
      .addCase(fetchAppConfig.rejected, (state, action) => {
        state.appConfigLoading = false;
        state.appConfigHydrated = true;
        state.appConfigError = action.payload;
      })
      .addCase(submitBusinessClaim.fulfilled, (state) => {
        const currentCount = Number(state.appConfig?.totalBusinessCount);
        state.appConfig = {
          ...(state.appConfig || {}),
          hasApprovedBusiness: false,
          totalBusinessCount: Math.max(1, Number.isFinite(currentCount) ? currentCount : 0),
        };
        state.appConfigHydrated = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.businesses = [];
        state.activeBusinessId = null;
        state.businessSwitcherSnapshot = null;
        state.businessesLoading = false;
        state.businessesHydrated = false;
        state.businessesError = null;
        clearPersistedActiveBusinessSnapshot();
        state.restaurantDetail = null;
        state.restaurantDetailTab = null;
        state.restaurantDetailLoading = false;
        state.restaurantDetailSaving = false;
        state.restaurantDetailError = null;
        state.reviews = [];
        state.reviewsSummary = null;
        state.reviewsPagination = null;
        state.reviewsLoading = false;
        state.reviewsError = null;
        state.appConfig = null;
        state.appConfigLoading = false;
        state.appConfigError = null;
        state.appConfigHydrated = false;
      });
  },
});

export const {
  setActiveBusinessId,
  switchActiveBusiness,
  clearRestaurantDetail,
  clearReviews,
  seedAdminBusinessContext,
} = merchantSlice.actions;

export const selectMerchantBusinesses = (state) => state.merchant.businesses;
export const selectBusinessesHydrated = (state) => state.merchant.businessesHydrated;
export const selectBusinessesLoading = (state) => state.merchant.businessesLoading;
export const selectActiveBusinessId = (state) => state.merchant.activeBusinessId;
export const selectActiveBusiness = (state) => {
  const { businesses, activeBusinessId, businessSwitcherSnapshot } = state.merchant;
  if (!activeBusinessId) return null;

  const fromList = businesses.find((item) => String(item.id) === String(activeBusinessId));
  if (fromList) return fromList;

  // Optimistic switch (e.g. notification click) before list refresh catches up.
  if (
    businessSwitcherSnapshot &&
    String(businessSwitcherSnapshot.id) === String(activeBusinessId)
  ) {
    return businessSwitcherSnapshot;
  }

  return null;
};

export const selectBusinessSwitcherDisplay = (state) => {
  const { businesses, businessesHydrated, businessSwitcherSnapshot } = state.merchant;
  const activeBusiness = selectActiveBusiness(state);

  if (activeBusiness) return activeBusiness;
  if (businessesHydrated && businesses.length === 0) return null;

  return businessSwitcherSnapshot;
};

export const selectAppConfig = (state) => state.merchant.appConfig;
export const selectAppConfigLoading = (state) => state.merchant.appConfigLoading;
export const selectAppConfigHydrated = (state) => state.merchant.appConfigHydrated;

/** True until app config and restaurant list have hydrated once. Refetches must not unmount the page. */
export const selectManageBusinessBootstrapLoading = (state) => {
  const merchant = state.merchant;

  return !merchant.appConfigHydrated || !merchant.businessesHydrated;
};

/** True when a restaurant detail record exists for the business (any tab). */
export const selectHasRestaurantDetailSnapshot = (state, businessId) => {
  if (!businessId) return false;

  const detail = state.merchant.restaurantDetail;
  return Boolean(detail && String(detail.id) === String(businessId));
};

/** True when restaurant detail is loaded and not refetching for the requested business id. */
export const selectIsRestaurantDetailReady = (state, businessId) => {
  if (!businessId) return true;
  if (state.merchant.restaurantDetailLoading) return false;

  return selectHasRestaurantDetailSnapshot(state, businessId);
};

/** True when detail is loaded for the business and matches the active profile tab. */
export const selectIsRestaurantDetailReadyForTab = (state, businessId, activeTab = null) => {
  if (!selectIsRestaurantDetailReady(state, businessId)) return false;
  if (!activeTab) return true;

  return state.merchant.restaurantDetailTab === activeTab;
};

/** Full-page skeleton — bootstrap plus the first detail fetch before any snapshot exists. */
export const selectManageBusinessInitialLoading = (state, businessId, activeTab = null) => {
  if (selectManageBusinessBootstrapLoading(state)) return true;
  if (!businessId) return false;
  if (selectIsRestaurantDetailReadyForTab(state, businessId, activeTab)) return false;
  if (state.merchant.restaurantDetailError) return false;

  return !selectHasRestaurantDetailSnapshot(state, businessId);
};

/** Tab skeleton — snapshot exists but belongs to a different profile tab. */
export const selectManageBusinessTabLoading = (state, businessId, activeTab = null) => {
  if (!businessId || !activeTab) return false;
  if (state.merchant.restaurantDetailError) return false;
  if (!selectHasRestaurantDetailSnapshot(state, businessId)) return false;

  return state.merchant.restaurantDetailTab !== activeTab;
};

export const selectRestaurantDetail = (state) => state.merchant.restaurantDetail;
export const selectRestaurantDetailTab = (state) => state.merchant.restaurantDetailTab;
export const selectRestaurantDetailLoading = (state) => state.merchant.restaurantDetailLoading;
export const selectRestaurantDetailSaving = (state) => state.merchant.restaurantDetailSaving;
export const selectRestaurantDetailError = (state) => state.merchant.restaurantDetailError;

export const selectReviews = (state) => state.merchant.reviews;
export const selectReviewsSummary = (state) => state.merchant.reviewsSummary;
export const selectReviewsPagination = (state) => state.merchant.reviewsPagination;
export const selectReviewsLoading = (state) => state.merchant.reviewsLoading;
export const selectReviewsError = (state) => state.merchant.reviewsError;

export const selectHasApprovedBusiness = (state) => {
  const config = state.merchant.appConfig;
  if (config && typeof config.hasApprovedBusiness === 'boolean') {
    return Boolean(config.hasApprovedBusiness);
  }

  if (state.auth.user?.hasApprovedBusiness) {
    return true;
  }

  if (!state.merchant.appConfigHydrated) {
    if (selectLikelyPendingReviewBeforeConfig(state)) {
      return false;
    }

    if (state.merchant.businessSwitcherSnapshot?.id) {
      return true;
    }
  }

  return false;
};

export const selectTotalBusinessCount = (state) =>
  parseTotalBusinessCount(state.merchant.appConfig?.totalBusinessCount);

export const selectMerchantConfigDestination = (state) => {
  if (!state.merchant.appConfigHydrated) return null;

  return resolveMerchantConfigDestination(state.merchant.appConfig);
};

export const selectNeedsBusinessSearch = (state) =>
  selectMerchantConfigDestination(state) === MERCHANT_CONFIG_DESTINATION.SEARCH_BUSINESS;

/** Waiting dashboard — under review (hasApprovedBusiness false, totalBusinessCount > 0). */
export const selectIsWaitingDashboard = (state) =>
  selectMerchantConfigDestination(state) === MERCHANT_CONFIG_DESTINATION.WAITING_DASHBOARD;

/** Approved full dashboard (hasApprovedBusiness true, totalBusinessCount > 0). */
export const selectIsApprovedDashboard = (state) =>
  selectMerchantConfigDestination(state) === MERCHANT_CONFIG_DESTINATION.DASHBOARD;

/** @deprecated Use selectIsWaitingDashboard */
export const selectIsLimitedAccessMode = selectIsWaitingDashboard;

export const resolveNeedsBusinessSearch = (config = null) =>
  resolveMerchantConfigDestination(config) === MERCHANT_CONFIG_DESTINATION.SEARCH_BUSINESS;

export const selectShouldRedirectToBusinessSearch = selectNeedsBusinessSearch;

const selectLikelyPendingReviewBeforeConfig = (state) => {
  const user = state.auth.user;
  const profileCompleted = state.onboarding.profileCompleted;

  return Boolean(
    state.onboarding.businessSearch.claimSubmitted ||
    (profileCompleted && user && user.hasApprovedBusiness === false),
  );
};

/** Pick the correct dashboard skeleton before app config hydrates on refresh */
export const selectLikelyPendingReviewDashboard = (state) => {
  if (state.merchant.appConfigHydrated) {
    return selectIsWaitingDashboard(state);
  }

  return selectLikelyPendingReviewBeforeConfig(state);
};

export const selectIsPendingReviewDashboard = selectIsWaitingDashboard;

/** Single loading gate for the pending-review dashboard (config + restaurant list). */
export const selectPendingReviewDashboardLoading = (state) => {
  if (!state.merchant.appConfigHydrated) {
    return selectLikelyPendingReviewDashboard(state);
  }

  if (!selectIsWaitingDashboard(state)) {
    return false;
  }

  return !state.merchant.businessesHydrated;
};

export default merchantSlice.reducer;
